import { createContext, useCallback, useContext, useState } from "react";
import type { ECN } from "@/data/changes";
import type { Insight } from "@/data/insights";

export interface AuditEntry {
  id: string;
  actionId: string;
  title: string;
  approver: string;
  ts: string;
  conversationId: string;
  summary: string;
}

interface ActionState {
  sessionEcns: ECN[];
  auditLog: AuditEntry[];
  sessionInsights: Insight[];
  reuseFlags: Set<string>;
  addEcn: (e: ECN) => void;
  addAudit: (e: AuditEntry) => void;
  addInsight: (i: Insight) => void;
  flagReuse: (partNumber: string) => void;
  resetDemo: () => void;
}

const Ctx = createContext<ActionState | null>(null);

export function ActionStoreProvider({ children }: { children: React.ReactNode }) {
  const [sessionEcns, setSessionEcns] = useState<ECN[]>([]);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [sessionInsights, setSessionInsights] = useState<Insight[]>([]);
  const [reuseFlags, setReuseFlags] = useState<Set<string>>(new Set());

  const addEcn = useCallback((e: ECN) => {
    setSessionEcns((p) => (p.some((x) => x.changeNumber === e.changeNumber) ? p : [e, ...p]));
  }, []);
  const addAudit = useCallback((e: AuditEntry) => setAuditLog((p) => [e, ...p]), []);
  const addInsight = useCallback(
    (i: Insight) => setSessionInsights((p) => (p.some((x) => x.id === i.id) ? p : [i, ...p])),
    [],
  );
  const flagReuse = useCallback(
    (pn: string) => setReuseFlags((p) => new Set(p).add(pn)),
    [],
  );
  const resetDemo = useCallback(() => {
    setSessionEcns([]);
    setAuditLog([]);
    setSessionInsights([]);
    setReuseFlags(new Set());
  }, []);

  return (
    <Ctx.Provider
      value={{ sessionEcns, auditLog, sessionInsights, reuseFlags, addEcn, addAudit, addInsight, flagReuse, resetDemo }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useActionStore() {
  const v = useContext(Ctx);
  if (!v) throw new Error("ActionStoreProvider missing");
  return v;
}
